import { errorCodes, getUserFriendlyErrorMessage } from '@/utils/errormessages'
import { apiErrorCodes } from '@/utils/constants'
import { isRecord } from '@/utils/narrowing'
import { text } from '../Constants'

/**
 * Typed reproduction of the error mapping the `IntakeFormListener` (and the
 * `intakeFormsReducer` GET/CREATE/SAVE error branch) ran for the intake-form flows.
 *
 * The Orval `fetcher` throws an `AppError` (`{ status, code, message, details }`) where
 * `details` is the parsed server body — the same object the old `apiCall` placed on
 * `action.payload`. We narrow that body and map it to the exact same friendly message the
 * page previously read from Redux / the listener's `setError`.
 */

const getString = (value: unknown): string => (typeof value === 'string' ? value : '')

/**
 * Pulls the parsed server body out of whatever the mutation/query rejected with.
 * AppError carries it on `details`; we also accept a bare body object as a fallback.
 */
export const getIntakeFormErrorBody = (error: unknown): Record<string, unknown> => {
  if (isRecord(error) && 'details' in error && isRecord(error.details)) {
    return error.details
  }
  if (isRecord(error)) {
    return error
  }
  return {}
}

/**
 * Mirrors the listener / reducer error branch:
 *  - UserFriendlyErrorCode present -> friendly message (or '' when the code is empty)
 *  - ErrorCode === NameNotUnique -> "Intake form already exists"
 *  - ErrorCode === IntakeFormNotFound -> "does not exist" (the message the view watches
 *    to navigate back to Management)
 *  - default -> generic default message
 */
export const mapIntakeFormError = (error: unknown): string => {
  const body = getIntakeFormErrorBody(error)

  const hasUserFriendlyCode =
    'UserFriendlyErrorCode' in body && (Boolean(body.UserFriendlyErrorCode) || body.UserFriendlyErrorCode === '')

  if (hasUserFriendlyCode) {
    const code = getString(body.UserFriendlyErrorCode)
    const extra = typeof body.extra === 'string' ? body.extra : undefined
    return code ? getUserFriendlyErrorMessage(code, null, extra) : ''
  }

  switch (getString(body.ErrorCode)) {
    case apiErrorCodes.NAME_NOT_UNIQUE:
      return getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Intake form')
    case apiErrorCodes.INTAKE_FORM_NOT_FOUND:
      return getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, text.INTAKE_FORM)
    default:
      return getUserFriendlyErrorMessage(errorCodes.DEFAULT)
  }
}
